import { useEffect, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { MdCheckCircle, MdErrorOutline, MdLockOutline } from "react-icons/md"
import api from "../../services/api"
import styles from "./googleOAuthCallback.module.css"
import GoogleBrandIcon from "./GoogleBrandIcon"

const decodeAccountId = state => {
    try {
        const encodedPayload = String(state || "").split(".")[1]
        const normalized = encodedPayload.replace(/-/g, "+").replace(/_/g, "/")
        return JSON.parse(window.atob(normalized))?.account_id || ""
    } catch { return "" }
}

const GoogleOAuthCallback = () => {
    const [searchParams] = useSearchParams()
    const [status, setStatus] = useState("processing")
    const [message, setMessage] = useState("Confirmando as permissões concedidas no Google…")
    const [fallbackPath, setFallbackPath] = useState("/")
    const authorizationStarted = useRef(false)

    useEffect(() => {
        if (authorizationStarted.current) return
        authorizationStarted.current = true
        const state = searchParams.get("state") || ""
        const accountId = sessionStorage.getItem("google_oauth_account_id") || decodeAccountId(state)
        const integrationPath = accountId ? `/${accountId}/integrations/google` : "/"
        setFallbackPath(integrationPath)
        sessionStorage.removeItem("google_oauth_account_id")

        const redirect = (result, detail = "") => {
            const query = new URLSearchParams({ google_oauth: result })
            if (detail) query.set("message", detail)
            window.setTimeout(() => window.location.replace(`${integrationPath}?${query}`), 900)
        }

        const completeAuthorization = async () => {
            const oauthError = searchParams.get("error")
            if (oauthError) {
                const denied = oauthError === "access_denied"
                setStatus(denied ? "denied" : "error")
                setMessage(denied ? "Você cancelou a autorização. Nenhum acesso foi concedido à Oktto." : "O Google não conseguiu concluir a autorização.")
                redirect(denied ? "denied" : "error")
                return
            }

            const code = searchParams.get("code")
            if (!code || !state || !accountId) {
                setStatus("error")
                setMessage("A resposta de autorização está incompleta ou expirou. Inicie a conexão novamente.")
                if (accountId) redirect("error", "A resposta de autorização está incompleta ou expirou.")
                return
            }

            try {
                await api.patch("/integrations/google/oauth/callback", { code, oauth_state: state, account_id: accountId })
                setStatus("success")
                setMessage("Conta conectada. As permissões do Google Ads e da Data Manager API foram confirmadas.")
                redirect("success")
            } catch (error) {
                const detail = error?.response?.data?.message || "Não foi possível concluir a conexão com o Google."
                setStatus("error")
                setMessage(detail)
                redirect("error", detail)
            }
        }

        completeAuthorization()
    }, [searchParams])

    return (
        <main className={styles.page}>
            <section className={styles.card} role="status" aria-live="polite">
                <div className={styles.googleIcon}><GoogleBrandIcon /></div>
                {status === "processing" && <span className={styles.spinner} aria-hidden="true" />}
                {status === "success" && <MdCheckCircle className={styles.successIcon} />}
                {(status === "error" || status === "denied") && <MdErrorOutline className={status === "denied" ? styles.deniedIcon : styles.errorIcon} />}
                <h1>{status === "processing" ? "Conectando ao Google" : status === "success" ? "Conexão concluída" : status === "denied" ? "Autorização cancelada" : "Não foi possível conectar"}</h1>
                <p>{message}</p>
                <div className={styles.securityNote}><MdLockOutline /><span>Sua senha do Google nunca é compartilhada com a Oktto.</span></div>
                {status === "error" && <Link to={fallbackPath}>Voltar para a integração</Link>}
            </section>
        </main>
    )
}

export default GoogleOAuthCallback
